/**
 * Demo Context
 * 
 * React context for managing demo state and interactions.
 */

import React, { createContext, useContext, ReactNode } from 'react';

export interface DemoSession {
  id: string;
  session_key: string;
  user_id?: string;  // Added user_id property
  current_scene: number;
  completed_scenes: number[];
  user_profile: Record<string, any>;
  session_data: Record<string, any>;
  demo_artifacts: any[];
  is_active: boolean;
  is_completed: boolean;
  lead_captured: boolean;
}

export interface DemoContextType {
  currentSession: DemoSession | null;
  currentScene: number;
  sceneData: Record<string, any> | null;
  isDemoActive: boolean;
  isLoading: boolean;
  error: string | null;
  processUserInput: (input: string) => Promise<void>;
  advanceScene: () => Promise<void>;
  handleCardAction: (action: string, payload: any) => Promise<void>;
  onDemoComplete: () => void;
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

interface DemoProviderProps {
  children: ReactNode;
  value: DemoContextType;
}

export const DemoProvider: React.FC<DemoProviderProps> = ({ children, value }) => {
  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
};

export const useDemoContext = (): DemoContextType => {
  const context = useContext(DemoContext);
  if (context === undefined) {
    throw new Error('useDemoContext must be used within a DemoProvider');
  }
  return context;
};

export { DemoContext };
export default DemoContext;
